import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useDispatch, useSelector } from "react-redux";
import {
  assignDriverService,
  driverSearchService,
} from "../features/outletSlice";
import toast from "react-hot-toast";
import { MobileResponsive } from "./MobileResponsive";
import useDataFetch from "../hooks/useDataFetch";
import AllocateNewDriverTable from "../components/AllocateNewDriverTable";
import {
  clearUnsubscribe,
  getDriverCoordinates,
} from "../features/driverSlice";
import { Map } from "@vis.gl/react-google-maps";
import { MapMarker } from "../components/MapMarker";
import { InfoWindowPopup } from "../components/InfoWindow";
import { getDefaultCenter, getEntityData } from "../utils/mapUtils";
import DynamicMetaTags from "../components/DynamicMetaTags";

const AllocateNewDriver = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [newDrivers, setNewDrivers] = useState([]);
  const [mapDrivers, setMapDrivers] = useState([]);
  const { data, isLoading } = useDataFetch(`/admin/order-detail/${id}`);

  const driverCoordinates = useSelector(
    (state) => state.drivers.driverCoordinates
  );

  useEffect(() => {
    if (data && data?.orderDTO?.status === "Cancelled") {
      fetchNearbyDrivers();
    }
  }, [data]);

  useEffect(() => {
    dispatch(getDriverCoordinates(process.env.REACT_APP_COLLECTION_DRIVER));

    const drivers = [];

    driverCoordinates.forEach((allDriver) => {
      const driver = newDrivers?.find(
        (driver) => driver?._id === allDriver?.id
      );
      if (driver) drivers.push({ ...allDriver, ...driver });
    });

    setMapDrivers(drivers);

    return () => {
      dispatch(clearUnsubscribe());
    };
  }, [dispatch, newDrivers]);

  /* Google Maps */
  const [selectedMarker, setSelectedMarker] = useState({
    selectedMarker: null,
    entityData: [],
  });
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const handleMarkerClick = (id, marker, entityType) => {
    setSelectedMarker(() => ({
      selectedMarker: marker,
      entityData: getEntityData(id, entityType, mapDrivers),
    }));
    setInfoWindowShown(true);
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowShown(false);
    setSelectedMarker({
      selectedMarker: null,
      entityData: null,
    });
  };

  const onMapClick = useCallback(() => {
    setSelectedMarker({
      selectedMarker: null,
      entityData: [],
    });
    setInfoWindowShown(false);
  }, []);

  useEffect(() => {
    if (
      selectedMarker.entityData?.length > 0 &&
      selectedMarker.selectedMarker
    ) {
      setInfoWindowShown(true);
    }
  }, [selectedMarker]);

  const fetchNearbyDrivers = async () => {
    const resultAction = await dispatch(
      driverSearchService({
        orderId: data?.orderDTO?.orderId,
        prevdriverId:
          data?.orderDTO?.order_rejected_by_drivers[
            data?.orderDTO?.order_rejected_by_drivers?.length - 1
          ],
      })
    );

    if (driverSearchService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      setNewDrivers(resultAction.payload?.drivers);
    }
  };

  const handleAssignDriver = async (newDriverId) => {
    const resultAction = await dispatch(
      assignDriverService({
        orderId: data?.orderDTO?.orderId,
        prevdriverId:
          data?.orderDTO?.order_rejected_by_drivers[
            data?.orderDTO?.order_rejected_by_drivers?.length - 1
          ],

        driverId: newDriverId,
      })
    );

    if (assignDriverService.fulfilled.match(resultAction)) {
      toast.success(resultAction?.payload?.message);
      navigate(`/orders/${data?.orderDTO?.orderId}`);
    }
  };

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Optimize Your Driver Management"
        pageDescription="Enhance your delivery operations with Beep! Manage new driver requests, view delivery history, track driver performance, and monitor weekly earnings. Suspend or delete drivers effortlessly."
        pageKeywords="Beep, driver management, new driver requests, delivery history, driver performance, suspend driver, delete driver, weekly earnings"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary">
          <div className="flex justify-between w-full">
            <Link
              to={`/outlets/${data?.orderDTO?.outletDetails?.id}`}
              className="flex items-center gap-3"
            >
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div>
              <span className="font-semibold text-base">
                Allocate new driver
              </span>
            </Link>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 w-full h-[75dvh]">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          ) : (
            <>
              <div className="w-full rounded-lg flex flex-col items-center justify-between gap-4 py-8">
                <div className="flex flex-col justify-center items-center gap-5 w-full h-[45vh] rounded-lg overflow-hidden shadow-default">
                  {isLoading ? (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                      <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse"></div>
                    </div>
                  ) : (
                    <Map
                      mapId={"terrain"}
                      defaultZoom={8}
                      defaultCenter={getDefaultCenter(mapDrivers, "hubspots")}
                      gestureHandling={"greedy"}
                      onClick={onMapClick}
                      disableDefaultUI
                      styles={"terrain"}
                      fullscreenControl={true}
                      mapTypeId={"terrain"}
                      mapTypeControlOptions
                      zoomControl
                      scaleControl
                      mapTypeControl
                      rotateControl
                    >
                      <MapMarker
                        data={mapDrivers}
                        entityType="drivers"
                        onMouseEnter={handleMarkerClick}
                      />

                      <InfoWindowPopup
                        infoWindowShown={infoWindowShown}
                        selectedMarker={selectedMarker}
                        handleCloseInfoWindow={handleCloseInfoWindow}
                      />
                    </Map>
                  )}
                </div>
              </div>
              <div className="w-full rounded-lg flex flex-col items-center justify-between gap-4 py-8">
                <div className="flex flex-col gap-5 w-full">
                  <AllocateNewDriverTable
                    headers={[
                      "Sr No.",
                      "Driver ID",
                      "Driver's name",
                      "Driver's phone no.",
                      "Deliveries done",
                    ]}
                    data={newDrivers}
                    eventHandler={handleAssignDriver}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AllocateNewDriver;
